/**
 * @file Shared React component — components/Layout/SidebarContributor.tsx.
 */

import React, { useState, useEffect } from "react";
import "./SidebarContributor.css";
import SidebarFooter from "./SidebarFooter";

interface SidebarContributorProps {
  onSelect: (section: string) => void;
  currentStage: number;
  submissionId?: string | null;
  activeSection?: string;
  stages?: string[];
}

const DEFAULT_STAGES = [
  "Contributor's Details",
  "Publication Details",
  "Background Data",
  "A1",
  "A2",
  "A3",
  "A1-A3",
  "Dataset",
];

const SidebarContributor: React.FC<SidebarContributorProps> = ({
  onSelect,
  currentStage,
  submissionId,
  activeSection = "Home",
  stages = DEFAULT_STAGES,
}) => {
  const [active, setActive] = useState(activeSection);

  useEffect(() => {
    // ✅ Sync active state with parent's activeSection
    if (activeSection) {
      setActive(activeSection);
    }
  }, [activeSection]);

  useEffect(() => {
    // ✅ Auto-highlight current stage when parent updates (only if we're on a stage)
    if (currentStage >= 0 && currentStage < stages.length && stages.includes(activeSection)) {
      setActive(stages[currentStage]);
    }
  }, [currentStage, activeSection, stages]);

  const handleClick = (section: string) => {
    setActive(section);
    onSelect(section);
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-inner">
        <button
          className={`menu-btn ${active === "Home" ? "active" : ""}`}
          onClick={() => handleClick("Home")}
        >
          Home
        </button>

        <button
          className={`menu-btn ${active === "Profile" ? "active" : ""}`}
          onClick={() => handleClick("Profile")}
        >
          My Profile
        </button>

        <button
          className={`menu-btn ${active === "IncompleteSubmissions" ? "active" : ""}`}
          onClick={() => handleClick("IncompleteSubmissions")}
        >
          Incomplete Datasets
        </button>

        <button
          className={`menu-btn ${active === "FlaggedSubmissions" ? "active" : ""}`}
          onClick={() => handleClick("FlaggedSubmissions")}
        >
          Flagged Datasets
        </button>

           <button
          className={`menu-btn ${active === "SubmittedDataset" ? "active" : ""}`}
          onClick={() => handleClick("SubmittedDataset")}
        >
          Submitted Datasets
        </button>

        <button
        style={{marginBottom:"0px"}}
          className={`menu-btn mb-0 ${
            active.startsWith("Data Input") ? "active" : ""
          }`}
          onClick={() => handleClick("Data Input")}
        >
          Data Input
        </button>

        {/* ✅ Stage Progress - Only show when submissionId is set */}
        {submissionId && (
          <div
            className={["progress-line", "stage-progress", `stage-current-${currentStage}`]
              .filter(Boolean)
              .join(" ")}
          >
            {stages.map((stage, index) => (
              <div
                key={stage}
                data-stage-value={stage}
                className={[
                  "stage-item",
                  `stage-index-${index}`,
                  active === stage ? "stage-active" : "",
                  index === currentStage ? "stage-current" : "",
                  index < currentStage ? "stage-completed" : "",
                  index > currentStage ? "stage-locked" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleClick(stage)}
              >
                <div className="dot"></div>
                <p>{stage}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Bottom Section */}
      <SidebarFooter />
    </aside>
  );
};

/** React component — Sidebar Contributor. */
export default SidebarContributor;
